import React, { useState, useEffect } from 'react';
import { Card, Button, Tag, notification, Table, Modal, Result } from 'antd';
import dayjs from 'dayjs';
import {
  CheckCircleOutlined,
  DollarOutlined,
  FileTextOutlined,
  UserOutlined,
  TagsOutlined,
  TeamOutlined,
  CalculatorOutlined,
  CalendarOutlined,
  EnvironmentOutlined
} from '@ant-design/icons';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { EstimatesUrl } from '../utils/network';
import { EstimateProps, EstimateLineItemProps } from '../utils/types';
import logo from '../assets/logo.png';
import { PageLoader } from '../components';

const PublicEstimateView: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [estimate, setEstimate] = useState<EstimateProps | null>(null);
  const [lineItems, setLineItems] = useState<EstimateLineItemProps[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);

  useEffect(() => {
    if (token) {
      fetchEstimate();
    }
  }, [token]);

  const fetchEstimate = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${EstimatesUrl}/public_view?token=${token}`);
      setEstimate(response.data);
      setLineItems(response.data.items || []);
      setError(null);

      // If estimate is already approved, show the modal
      if (response.data.status === 'approved') {
        setShowApprovalModal(true);
      }
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to load estimate');
      // Only show notification if estimate is not approved (to avoid showing error for approved estimates)
      if (!(error.response?.data?.status === 'approved')) {
        notification.error({
          message: 'Error',
          description: error.response?.data?.error || 'Failed to load estimate',
          title: 'Error'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = () => {
    Modal.confirm({
      title: 'Approve Estimate',
      content: 'Are you sure you want to approve this estimate? This action cannot be undone.',
      okText: 'Approve',
      okType: 'primary',
      onOk: async () => {
        setActionLoading(true);
        try {
          await axios.post(`${EstimatesUrl}/customer_approve`, { token });
          fetchEstimate();
          setShowApprovalModal(true);
        } catch (error: any) {
          notification.error({
            message: 'Approval Error',
            description: error.response?.data?.error || 'Failed to approve estimate',
            title: 'Error'
          });
        } finally {
          setActionLoading(false);
        }
      }
    });
  };

  const columns = [
    {
      title: 'Charge',
      dataIndex: 'charge_name',
      key: 'charge_name',
      render: (name: string, record: EstimateLineItemProps) => (
        <div>
          <div style={{ fontWeight: 600 }}>{name}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {record.category_name}
          </div>
        </div>
      )
    },
    {
      title: 'Type',
      dataIndex: 'charge_type',
      key: 'charge_type',
      width: 120,
      render: (type: string) => (
        <Tag color="blue">{type.replace('_', ' ').toUpperCase()}</Tag>
      )
    },
    {
      title: 'Rate',
      dataIndex: 'rate',
      key: 'rate',
      width: 120,
      render: (rate: number, record: EstimateLineItemProps) => {
        if (record.charge_type === 'percent') return '-';
        return <span>${rate ? Number(rate).toFixed(2) : '0.00'}</span>;
      }
    },
    {
      title: 'Percentage',
      dataIndex: 'percentage',
      key: 'percentage',
      width: 120,
      render: (percentage: number, record: EstimateLineItemProps) => {
        if (record.charge_type !== 'percent') return '-';
        return <span>{percentage ? Number(percentage).toFixed(2) : '0.00'}%</span>;
      }
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 100,
      render: (quantity: number) => <span>{quantity ? Number(quantity).toFixed(2) : '1.00'}</span>
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
      render: (amount: number) => {
        const amountNum = amount ? Number(amount) : 0;
        return (
          <span style={{ fontWeight: 600, color: '#52c41a' }}>
            ${amountNum.toFixed(2)}
          </span>
        );
      }
    }
  ];

  if (loading) {
    return <PageLoader fullPage text="Loading estimate..." />;
  }

  // Don't show error if estimate is approved - show the content with modal instead
  if (error && !(estimate && estimate.status === 'approved')) {
    return (
      <div style={{ padding: '60px' }}>
        <Result
          status="error"
          title="Unable to Load Estimate"
          subTitle={error}
        />
      </div>
    );
  }

  if (!estimate) {
    return null;
  }

  const isAlreadyResponded = estimate.status === 'approved' || estimate.status === 'rejected';

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f5f5f5', padding: '24px' }}>
      <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
          borderRadius: '12px',
          padding: '32px',
          marginBottom: '24px',
          color: '#fff'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ marginBottom: '16px' }}>
              <img
                src={logo}
                alt="Baltic Van Lines"
                style={{ maxHeight: '60px', width: 'auto' }}
              />
            </div>
            {estimate.customer_job_number && (
              <div style={{ fontSize: '20px', fontWeight: 600, color: '#fff', marginBottom: '8px' }}>
                Job Number: {estimate.customer_job_number}
              </div>
            )}
            <Tag
              color={
                estimate.status === 'approved' ? 'green' :
                  estimate.status === 'rejected' ? 'red' :
                    estimate.status === 'sent' ? 'blue' : 'orange'
              }
              style={{ fontSize: '12px' }}
            >
              {estimate.status?.toUpperCase()}
            </Tag>
          </div>
        </div>

        {/* Customer & Estimate Info */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '24px' }}>
          <Card style={{ borderRadius: '12px' }}>
            <h3 style={{ marginTop: 0, fontSize: '15px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <UserOutlined style={{ color: '#1890ff' }} /> Customer Information
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <div style={{ fontSize: '11px', color: '#999', marginBottom: '4px' }}>Name</div>
                <div style={{ fontSize: '14px', fontWeight: 600 }}>{estimate.customer_name}</div>
              </div>
              {estimate.service_type_name && (
                <div>
                  <div style={{ fontSize: '11px', color: '#999', marginBottom: '4px' }}>Service Type</div>
                  <Tag color="purple">{estimate.service_type_name}</Tag>
                </div>
              )}
            </div>
          </Card>

          <Card style={{ borderRadius: '12px' }}>
            <h3 style={{ marginTop: 0, fontSize: '15px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <CalculatorOutlined style={{ color: '#52c41a' }} /> Estimate Details
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {estimate.weight_lbs && (
                <div>
                  <div style={{ fontSize: '11px', color: '#999', marginBottom: '4px' }}>Weight</div>
                  <div style={{ fontSize: '14px', fontWeight: 600 }}>{estimate.weight_lbs} lbs</div>
                </div>
              )}
              {estimate.labour_hours && (
                <div>
                  <div style={{ fontSize: '11px', color: '#999', marginBottom: '4px' }}>Labour Hours</div>
                  <div style={{ fontSize: '14px', fontWeight: 600 }}>{Number(estimate.labour_hours).toFixed(1)} hours</div>
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* Service Type Estimate Content */}
        {estimate.service_type_estimate_content && (
          <Card style={{ borderRadius: '12px', marginBottom: '24px' }}>
            <div
              style={{
                fontSize: '14px',
                color: '#333',
                lineHeight: '1.8',
                whiteSpace: 'pre-wrap',
                textAlign: 'center',
                padding: '16px'
              }}
            >
              {estimate.service_type_estimate_content}
            </div>
          </Card>
        )}

        {/* Customer Notes */}
        {estimate.customer_notes && (
          <Card
            style={{ borderRadius: '12px', marginBottom: '24px', backgroundColor: '#fffbe6', border: '1px solid #ffe58f' }}
            title={<span style={{ fontWeight: 600 }}>Notes & Special Instructions</span>}
          >
            <div
              style={{
                fontSize: '14px',
                color: '#333',
                lineHeight: '1.6',
                whiteSpace: 'pre-wrap'
              }}
            >
              {estimate.customer_notes}
            </div>
          </Card>
        )}

        {/* Origin and Destination Addresses */}
        {(estimate.origin_address || estimate.destination_address) && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '24px' }}>
            {estimate.origin_address && (
              <Card style={{ borderRadius: '12px' }}>
                <h3 style={{ marginTop: 0, fontSize: '15px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <EnvironmentOutlined style={{ color: '#52c41a' }} /> Origin Address
                </h3>
                <div style={{ fontSize: '14px', color: '#333', lineHeight: '1.6' }}>
                  {estimate.origin_address}
                </div>
              </Card>
            )}

            {estimate.destination_address && (
              <Card style={{ borderRadius: '12px' }}>
                <h3 style={{ marginTop: 0, fontSize: '15px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <EnvironmentOutlined style={{ color: '#1890ff' }} /> Destination Address
                </h3>
                <div style={{ fontSize: '14px', color: '#333', lineHeight: '1.6' }}>
                  {estimate.destination_address}
                </div>
              </Card>
            )}
          </div>
        )}

        {/* Pickup and Delivery Windows */}
        {(estimate.pickup_date_from || estimate.delivery_date_from) && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '24px' }}>
            {estimate.pickup_date_from && (
              <Card style={{ borderRadius: '12px' }}>
                <h3 style={{ marginTop: 0, fontSize: '15px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <CalendarOutlined style={{ color: '#fa8c16' }} /> Pickup Window
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div>
                    <div style={{ fontSize: '11px', color: '#999', marginBottom: '4px' }}>From</div>
                    <div style={{ fontSize: '14px', fontWeight: 600 }}>
                      {dayjs(estimate.pickup_date_from, 'YYYY-MM-DD').format('MMMM D, YYYY')}
                    </div>
                  </div>
                  {estimate.pickup_date_to && (
                    <div>
                      <div style={{ fontSize: '11px', color: '#999', marginBottom: '4px' }}>To</div>
                      <div style={{ fontSize: '14px', fontWeight: 600 }}>
                        {dayjs(estimate.pickup_date_to, 'YYYY-MM-DD').format('MMMM D, YYYY')}
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            )}

            {estimate.delivery_date_from && (
              <Card style={{ borderRadius: '12px' }}>
                <h3 style={{ marginTop: 0, fontSize: '15px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <CalendarOutlined style={{ color: '#13c2c2' }} /> Delivery Window
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div>
                    <div style={{ fontSize: '11px', color: '#999', marginBottom: '4px' }}>From</div>
                    <div style={{ fontSize: '14px', fontWeight: 600 }}>
                      {dayjs(estimate.delivery_date_from, 'YYYY-MM-DD').format('MMMM D, YYYY')}
                    </div>
                  </div>
                  {estimate.delivery_date_to && (
                    <div>
                      <div style={{ fontSize: '11px', color: '#999', marginBottom: '4px' }}>To</div>
                      <div style={{ fontSize: '14px', fontWeight: 600 }}>
                        {dayjs(estimate.delivery_date_to, 'YYYY-MM-DD').format('MMMM D, YYYY')}
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            )}
          </div>
        )}

        {/* Line Items */}
        <Card
          style={{ borderRadius: '12px', marginBottom: '24px' }}
          bodyStyle={{ padding: 0 }}
        >
          <div style={{ padding: '16px', borderBottom: '1px solid #f0f0f0' }}>
            <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 600 }}>
              <FileTextOutlined /> Estimate Breakdown
            </h3>
          </div>
          <Table
            columns={columns}
            dataSource={lineItems}
            loading={loading}
            rowKey="id"
            size="small"
            pagination={false}
            summary={() => {
              const subtotal = estimate.subtotal ? Number(estimate.subtotal) : 0;
              const discountAmount = estimate.discount_amount ? Number(estimate.discount_amount) : 0;
              const taxAmount = estimate.tax_amount ? Number(estimate.tax_amount) : 0;
              const taxPercentage = estimate.tax_percentage ? Number(estimate.tax_percentage) : 0;
              const totalAmount = estimate.total_amount ? Number(estimate.total_amount) : 0;

              return (
                <Table.Summary>
                  {/* Subtotal Row */}
                  <Table.Summary.Row style={{ backgroundColor: '#fafafa' }}>
                    <Table.Summary.Cell index={0} colSpan={5}>
                      <strong style={{ fontSize: '14px' }}>Subtotal</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={1}>
                      <strong style={{ fontSize: '16px', color: '#1890ff' }}>
                        ${subtotal.toFixed(2)}
                      </strong>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>

                  {/* Discount Row */}
                  {discountAmount > 0 && (
                    <Table.Summary.Row style={{ backgroundColor: '#fff1f0' }}>
                      <Table.Summary.Cell index={0} colSpan={5}>
                        <strong style={{ fontSize: '14px', color: '#cf1322' }}>
                          Discount
                          {estimate.discount_type === 'percent' && estimate.discount_value && (
                            <span style={{ marginLeft: '8px', fontWeight: 'normal' }}>
                              ({estimate.discount_value}%)
                            </span>
                          )}
                        </strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={1}>
                        <strong style={{ fontSize: '16px', color: '#cf1322' }}>
                          -${discountAmount.toFixed(2)}
                        </strong>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  )}

                  {/* Tax Row */}
                  {taxAmount > 0 && (
                    <Table.Summary.Row style={{ backgroundColor: '#fff7e6' }}>
                      <Table.Summary.Cell index={0} colSpan={5}>
                        <strong style={{ fontSize: '14px', color: '#d46b08' }}>
                          Sales Tax ({taxPercentage.toFixed(2)}%)
                        </strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={1}>
                        <strong style={{ fontSize: '16px', color: '#d46b08' }}>
                          ${taxAmount.toFixed(2)}
                        </strong>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  )}

                  {/* Total Row */}
                  <Table.Summary.Row style={{ backgroundColor: '#f6ffed' }}>
                    <Table.Summary.Cell index={0} colSpan={5}>
                      <strong style={{ fontSize: '16px' }}>Total Amount</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={1}>
                      <strong style={{ fontSize: '20px', color: '#52c41a' }}>
                        ${totalAmount.toFixed(2)}
                      </strong>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              );
            }}
          />
        </Card>

        {/* Action Buttons or Response Message */}
        {isAlreadyResponded ? (
          // For approved estimates, the modal will show the message
          // For rejected estimates, show the Result component
          estimate.status === 'rejected' ? (
            <Result
              status="warning"
              title="Estimate Rejected"
              subTitle="This estimate has been rejected. Thank you for your time."
            />
          ) : null
        ) : (
          <Card style={{ borderRadius: '12px' }}>
            <div style={{ textAlign: 'center' }}>
              <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>
                Do you approve this estimate?
              </h3>
              <p style={{ color: '#666', marginBottom: '24px' }}>
                Please review the details above and let us know your decision.
              </p>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                <Button
                  type="primary"
                  size="large"
                  icon={<CheckCircleOutlined />}
                  onClick={handleApprove}
                  loading={actionLoading}
                  style={{ minWidth: '150px' }}
                >
                  Approve Estimate
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Approval Success Modal */}
        <Modal
          open={showApprovalModal}
          onCancel={() => setShowApprovalModal(false)}
          footer={[
            <Button
              key="close"
              type="primary"
              onClick={() => setShowApprovalModal(false)}
              size="large"
            >
              Close
            </Button>
          ]}
          width={500}
          centered
        >
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <CheckCircleOutlined style={{ fontSize: '64px', color: '#52c41a', marginBottom: '24px' }} />
            <h2 style={{ fontSize: '24px', fontWeight: 600, marginBottom: '16px', color: '#000' }}>
              Thank you for your feedback!
            </h2>
            <div style={{ fontSize: '16px', color: '#333', lineHeight: '1.8', marginBottom: '8px' }}>
              <p style={{ marginBottom: '12px' }}>Your moving advisor will be notified of your input.</p>
              <p style={{ marginBottom: '12px' }}>To get your move secured we do require a deposit.</p>
              <p style={{ marginBottom: '0' }}>Please contact your moving advisor to take care of the next steps!</p>
            </div>
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default PublicEstimateView;

